import { zodResolver } from '@hookform/resolvers/zod'
import type { ClientDeviceWithDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import type { Device } from '@maru44/huntre-utils/src/models/device'
import {
  BankAccountType,
  BUYBACK_MAX_ITEMS,
  BUYBACK_MAXIMUM_PRICE,
  BUYBACK_MIN_AGE,
  BUYBACK_MINIMUM_PRICE,
  IdentificationKind,
  type IdentificationSide,
  isAccountHolderMatchingSeller,
  isUnderAge,
  MY_NUMBER_BACK_NOTICE,
  requiredIdentificationSides,
  textIdentificationKind,
  textIdentificationSide,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { type Control, Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { AuthContext } from 'src/components/providers/AuthProvider'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { buybackDeviceTypeIds, kobutsuLicenseText } from 'src/config'
import { useClientDevices } from 'src/hooks/clientDevice/useClientDevices'
import { useDeviceBuyback } from 'src/hooks/deviceBuyback/useDeviceBuyback'
import { useDeviceBuybacks } from 'src/hooks/deviceBuyback/useDeviceBuybacks'
import { z } from 'zod'
import { BuybackGuide } from '../DeviceBuybacks/BuybackGuide'

/**
 * 他のフォームと同じく zod + useForm で組む (.claude/rules/web.md)。
 *
 * **検証をスキーマに寄せているのは、エラーを項目の下に出すため。**
 * 以前は手書きの条件式でボタンを disable していただけで、
 * 「何が足りないのか」が画面から分からなかった。
 *
 * 本人確認書類の画像だけは `File` なのでスキーマに入れず、別の state で持つ。
 */
const schema = z
  .object({
    deviceIds: z.array(z.string()).min(1, 'デバイスを選択してください').max(BUYBACK_MAX_ITEMS, `1 回の申請に出せるのは ${BUYBACK_MAX_ITEMS} 台までです`),

    name: z.string().min(1, 'お名前を入力してください'),
    nameKana: z.string().min(1, 'フリガナを入力してください'),
    postalCode: z.string().regex(/^\d{7}$/, 'ハイフンなしの 7 桁で入力してください'),
    address: z.string().min(1, 'ご住所を入力してください'),
    occupation: z.string().min(1, 'ご職業を入力してください'),
    birthday: z.string().min(1, '生年月日を入力してください'),
    phone: z.string().min(1, '電話番号を入力してください'),

    bankName: z.string().min(1, '銀行名を入力してください'),
    branchName: z.string().min(1, '支店名を入力してください'),
    accountType: z.nativeEnum(BankAccountType),
    accountNumber: z.string().min(1, '口座番号を入力してください'),
    accountHolderKana: z.string().min(1, '口座名義を入力してください'),

    identificationKind: z.nativeEnum(IdentificationKind),
    applicantNote: z.string(),

    agreedPrice: z.boolean(),
    agreedIdentity: z.boolean(),
    agreedIndividual: z.boolean(),
  })
  .refine(({ birthday }) => !birthday || !isUnderAge(birthday, new Date()), {
    message: `${BUYBACK_MIN_AGE} 歳未満の方からは買い取りできません`,
    path: ['birthday'],
  })
  // 非対面の本人確認は「書類の画像 + 書類記載の本人名義口座への振込」の組み合わせで
  // しか成立しない。サーバも同じ判定で却下するが、**申し込む前に教えたほうが親切**
  .refine(({ nameKana, accountHolderKana }) => !nameKana || !accountHolderKana || isAccountHolderMatchingSeller(nameKana, accountHolderKana), {
    message: 'お名前のフリガナと一致していません。ご本人名義の口座をご入力ください',
    path: ['accountHolderKana'],
  })
  .refine(({ agreedPrice }) => agreedPrice, { message: '買取金額の条件への同意が必要です', path: ['agreedPrice'] })
  .refine(({ agreedIdentity }) => agreedIdentity, { message: '名義の確認への同意が必要です', path: ['agreedIdentity'] })
  .refine(({ agreedIndividual }) => agreedIndividual, { message: '個人としての売却であることの確認が必要です', path: ['agreedIndividual'] })

type FormState = z.infer<typeof schema>

/** TextField に渡せる = 値が文字列の項目だけ */
type TextFieldName = { [K in keyof FormState]: FormState[K] extends string ? K : never }[keyof FormState]
type AgreementName = { [K in keyof FormState]: FormState[K] extends boolean ? K : never }[keyof FormState]

const defaultValues: FormState = {
  deviceIds: [],
  name: '',
  nameKana: '',
  postalCode: '',
  address: '',
  occupation: '',
  birthday: '',
  phone: '',
  bankName: '',
  branchName: '',
  accountType: BankAccountType.Ordinary,
  accountNumber: '',
  accountHolderKana: '',
  identificationKind: IdentificationKind.DriversLicense,
  applicantNote: '',
  agreedPrice: false,
  agreedIdentity: false,
  agreedIndividual: false,
}

export const DeviceBuybackNewPage = () => {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const [user] = useContext(AuthContext)
  const { clientShippingAddresses } = useContext(ClientContext)

  const { listClientDevicesWithDevice } = useClientDevices(clientId ?? '')
  const { createDeviceBuyback, listSellableDeviceIds } = useDeviceBuybacks(clientId, user?.uid)

  const { control, handleSubmit, formState, watch, setValue, getValues } = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'all',
  })

  // 画像は File なのでスキーマに入れられない。別に持って submit 前に揃っているか見る
  const [files, setFiles] = useState<Partial<Record<IdentificationSide, File | null>>>({})
  const [sellable, setSellable] = useState<{ deviceId: string; device: Device }[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createdId, setCreatedId] = useState<string | undefined>()
  const { uploadIdentification } = useDeviceBuyback(clientId, createdId)

  const identificationKind = watch('identificationKind')
  const deviceIds = watch('deviceIds')

  const requiredSides = useMemo(() => requiredIdentificationSides(identificationKind), [identificationKind])
  const hasAllSides = requiredSides.every((side) => !!files[side])

  // 既定の送付先があれば郵便番号と住所を初期値にする
  useEffect(() => {
    const def = clientShippingAddresses.find((v) => v.isDefault) ?? clientShippingAddresses[0]
    if (!def) return
    if (getValues('postalCode') || getValues('address')) return
    setValue('postalCode', def.postalCode, { shouldValidate: true })
    setValue('address', def.address, { shouldValidate: true })
  }, [clientShippingAddresses, getValues, setValue])

  // 買取対象の deviceType かつ、進行中の申請が無いデバイスだけ出す
  useEffect(() => {
    const load = async () => {
      const rows = (listClientDevicesWithDevice.data ?? []).filter((v: ClientDeviceWithDevice) => buybackDeviceTypeIds.includes(v.device.type))
      const ids = await listSellableDeviceIds(rows.map((v: ClientDeviceWithDevice) => v.device.id))
      setSellable(
        rows.filter((v: ClientDeviceWithDevice) => ids.includes(v.device.id)).map((v: ClientDeviceWithDevice) => ({ deviceId: v.device.id, device: v.device })),
      )
    }
    load()
  }, [listClientDevicesWithDevice.data, listSellableDeviceIds])

  const onSubmit = handleSubmit(async (v) => {
    const devices = sellable.filter((s) => v.deviceIds.includes(s.deviceId))
    if (devices.length === 0 || !hasAllSides) return

    setIsSubmitting(true)
    try {
      // 申請を作ってから画像を上げる。
      // Storage のルールが clients/{clientId}/deviceBuybacks/{id}.applicantId を見るため、
      // ドキュメントが無いとアップロードできない
      const id = await createDeviceBuyback({
        clientId: clientId ?? '',
        devices: devices.map((s) => ({ deviceId: s.deviceId, deviceTypeId: s.device.type })),
        seller: {
          name: v.name,
          nameKana: v.nameKana,
          postalCode: v.postalCode,
          address: v.address,
          occupation: v.occupation,
          birthday: v.birthday,
          phone: v.phone,
        },
        identificationKind: v.identificationKind,
        bankAccount: {
          bankName: v.bankName,
          branchName: v.branchName,
          accountType: v.accountType,
          accountNumber: v.accountNumber,
          accountHolderKana: v.accountHolderKana,
        },
        returnAddress: { postalCode: v.postalCode, address: v.address },
        applicantNote: v.applicantNote,
      })
      setCreatedId(id)
      toast.success('申請しました')
      navigate(`/clients/${clientId}/buybacks/${id}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '申請に失敗しました')
    } finally {
      setIsSubmitting(false)
    }
  })

  // 申請ができてから画像を上げる。
  // Storage のルールが clients/{clientId}/deviceBuybacks/{id}.applicantId を見るため、先に作る必要がある
  useEffect(() => {
    if (!createdId) return
    const upload = async () => {
      for (const side of requiredSides) {
        const f = files[side]
        if (f) await uploadIdentification(side, f)
      }
    }
    upload().catch(() => toast.error('本人確認書類のアップロードに失敗しました'))
  }, [createdId, files, requiredSides, uploadIdentification])

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={3}>
        <Typography variant="h5">買取を申し込む</Typography>

        {/*
          説明は一覧ページと同じ BuybackGuide を使う。文面を 2 箇所に持つと片方だけ
          古くなるため。ここはフォームが主役なのでたたんでおく
        */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              買取の条件と流れをご確認ください
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <BuybackGuide />
          </AccordionDetails>
        </Accordion>

        <Section title="1. デバイスを選ぶ" caption={`複数選べます (最大 ${BUYBACK_MAX_ITEMS} 台)。まとめて発送し、合計額を 1 回で振り込みます`}>
          {sellable.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              買取に出せるデバイスがありません。
            </Typography>
          ) : (
            <Controller
              name="deviceIds"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Stack>
                    {sellable.map((v) => (
                      <FormControlLabel
                        key={v.deviceId}
                        control={
                          <Checkbox
                            name={`deviceIds_${v.deviceId}`}
                            checked={field.value.includes(v.deviceId)}
                            onChange={() =>
                              field.onChange(field.value.includes(v.deviceId) ? field.value.filter((id) => id !== v.deviceId) : [...field.value, v.deviceId])
                            }
                          />
                        }
                        label={v.deviceId}
                      />
                    ))}
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {field.value.length} 台を選択中
                    {field.value.length > 0 && ` (最低保証 合計 ${(BUYBACK_MINIMUM_PRICE * field.value.length).toLocaleString()} 円)`}
                  </Typography>
                  {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
                </>
              )}
            />
          )}
        </Section>

        <Section title="2. お客様の情報" caption="古物営業法により、氏名・住所・職業・年齢の記録が必要です">
          <Field control={control} name="name" label="氏名" />
          <Field control={control} name="nameKana" label="氏名 (カナ)" />
          <Field control={control} name="postalCode" label="郵便番号 (ハイフンなし)" />
          <Field control={control} name="address" label="住所" />
          <Field control={control} name="occupation" label="職業" />
          <Field control={control} name="birthday" label="生年月日" type="date" />
          <Field control={control} name="phone" label="電話番号" />
        </Section>

        <Section title="3. 振込先口座" caption="法令により、本人確認書類に記載された氏名と同一名義の口座にお振込みします。名義が一致しない場合は買取できません">
          <Field control={control} name="bankName" label="銀行名" />
          <Field control={control} name="branchName" label="支店名" />
          <Controller
            name="accountType"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} component="label" htmlFor="accountType">
                  口座種別
                </Typography>
                <Select fullWidth size="small" id="accountType" {...field}>
                  <MenuItem value={BankAccountType.Ordinary}>普通</MenuItem>
                  <MenuItem value={BankAccountType.Checking}>当座</MenuItem>
                </Select>
              </Box>
            )}
          />
          <Field control={control} name="accountNumber" label="口座番号" />
          <Field control={control} name="accountHolderKana" label="口座名義 (全角カナ)" />
        </Section>

        <Section title="4. 本人確認書類" caption="書類に記載された氏名・住所と、上のご入力内容が一致している必要があります">
          <Controller
            name="identificationKind"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} component="label" htmlFor="identificationKind">
                  書類の種別
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  id="identificationKind"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    // 要求される面が変わるので選び直してもらう
                    setFiles({})
                  }}
                >
                  {Object.values(IdentificationKind).map((v) => (
                    <MenuItem key={v} value={v}>
                      {textIdentificationKind(v)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          />

          {/* 要求する面は書類で変わる。マイナンバーカードは表のみ */}
          {requiredSides.map((side) => (
            <Box key={side}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} component="label" htmlFor={`identification_${side}`}>
                {textIdentificationSide(identificationKind, side)}
              </Typography>
              <Box>
                <input
                  type="file"
                  accept="image/*"
                  id={`identification_${side}`}
                  name={`identification_${side}`}
                  onChange={(e) => setFiles((p) => ({ ...p, [side]: e.target.files?.[0] ?? null }))}
                />
              </Box>
            </Box>
          ))}
          {/* 画像はスキーマの外なので、不足はここで伝える */}
          {!hasAllSides && <FormHelperText error>必要な面の画像を選択してください</FormHelperText>}

          {identificationKind === IdentificationKind.MyNumberCard && <Alert severity="warning">{MY_NUMBER_BACK_NOTICE}</Alert>}
        </Section>

        <Section title="5. 確認">
          <Field control={control} name="applicantNote" label="備考 (傷の申告など・任意)" multiline />
          <Divider />
          <Agreement
            control={control}
            name="agreedPrice"
            label={`提示された買取金額 (1 台あたり 最低 ${BUYBACK_MINIMUM_PRICE.toLocaleString()} 円、上限 ${BUYBACK_MAXIMUM_PRICE.toLocaleString()} 円) に同意します`}
          />
          <Agreement control={control} name="agreedIdentity" label="申請者本人の名義であり、本人確認書類・口座名義と一致しています" />
          <Agreement control={control} name="agreedIndividual" label="個人として売却します (法人ではありません)" />
          <Alert severity="warning">
            本人確認が完了しましたら送付先をお知らせします。
            <strong>それまでデバイスは発送しないでください。</strong>
          </Alert>
          <Box>
            <Button type="submit" variant="contained" size="large" disabled={!formState.isValid || !hasAllSides || isSubmitting}>
              申請する
            </Button>
            {deviceIds.length === 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                デバイスを選ぶと申請できます
              </Typography>
            )}
          </Box>
        </Section>

        {/* 古物営業法 12 条 2 項。footer にも出しているが、申込みを受ける画面にも置く */}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          古物商許可 {kobutsuLicenseText}
        </Typography>
      </Stack>
    </Box>
  )
}

const Section = ({ title, caption, children }: { title: string; caption?: string; children: React.ReactNode }) => (
  <Paper sx={{ p: 3 }}>
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {caption}
          </Typography>
        )}
      </Box>
      {children}
    </Stack>
  </Paper>
)

/**
 * `{...field}` が name を spread するので、ここで name を書く必要はない
 * (.claude/rules/web.md の「name を書かなくてよい 2 つのケース」)。
 */
const Field = ({
  control,
  name,
  label,
  type,
  multiline,
}: {
  control: Control<FormState>
  name: TextFieldName
  label: string
  type?: string
  multiline?: boolean
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} component="label" htmlFor={name}>
          {label}
        </Typography>
        <TextField
          fullWidth
          size="small"
          id={name}
          type={type}
          {...field}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      </Box>
    )}
  />
)

const Agreement = ({ control, name, label }: { control: Control<FormState>; name: AgreementName; label: string }) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <Box>
        <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={label} />
        {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
      </Box>
    )}
  />
)
